import {
  getGameByGameIdEventsOptions,
  postDataTagMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@win-reality/react-ui";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { EditableTable, type TableGroup } from "@/components/EditableTable";
import { NavbarDescriptionPortal } from "@/components/layout/NavbarDescriptionPortal";
import { AlertMessage } from "@/components/ui/AlertMessage";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Spinner } from "@/components/ui/spinner";
import { TableCell, TableRow } from "@/components/ui/Table";
import { useGameEvents } from "@/hooks/useGameEvents";

import { buildGameTypeConfigs, type TagRowData } from "./game-type-configs";
import { applyRowResults } from "./notAPitch";
import { toGamePlayerFields, toTaggedPlayerFields } from "./playerOptions";
import { useGamePlayerOptions } from "./useGamePlayerOptions";
import { useTransformedTagsData } from "./useTransformedTagsData";

// Constants
const HALF_OPTIONS = [
  { value: "all", label: "All", id: "all" },
  { value: "top", label: "Top", id: "top" },
  { value: "bottom", label: "Bottom", id: "bottom" },
];
const ITEMS_PER_PAGE = 15;
const SAVE_BATCH_SIZE = 2;

const EditEventLog = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const queryClient = useQueryClient();
  const [selectedInning, setSelectedInning] = useState<string>("all");
  const [selectedHalf, setSelectedHalf] = useState<string>("all");
  const [selectedPitchType, setSelectedPitchType] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const { events: tags, game, isLoading, error } = useGameEvents({ gameId });
  const {
    getPlayersForField,
    availablePlayers,
    getRosterPlayer,
    getGamePlayer,
    isLoading: isLoadingRosters,
  } = useGamePlayerOptions(game);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedInning, selectedHalf, selectedPitchType, searchQuery]);

  const originalTagLookup = useMemo(() => {
    const m = new Map<string, unknown>();
    (tags ?? []).forEach((ev: unknown) =>
      m.set((ev as { event_uuid: string }).event_uuid, ev),
    );
    return m;
  }, [tags]);

  // React Query mutation for /data/tag
  const { mutateAsync: saveTag } = useMutation(postDataTagMutation());

  const buildPayload = (row: TagRowData) => {
    const original = originalTagLookup.get(row.eventUuid);
    if (!original) return null;

    const payload = JSON.parse(JSON.stringify(original));

    if (!payload.tag_data) payload.tag_data = {};
    if (!payload.tag_data.gameState) payload.tag_data.gameState = {};
    if (!payload.tag_data.metadata) payload.tag_data.metadata = {};

    const storedNames = row as Record<string, unknown>;

    // A picked player replaces all of the tag's player fields, so the tag
    // keeps none of the previous player's sides or jersey number
    const applyPlayer = (
      key: "currentPitcher" | "currentHitter",
      selected: string | undefined,
      stored: { name?: string; externalId?: string },
    ) => {
      if (!selected) return;
      const { gameState } = payload.tag_data;
      if (selected === "Unknown") {
        gameState[key] = null;
        return;
      }
      if (!gameState[key]) gameState[key] = {};
      const rosterPlayer = getRosterPlayer(selected);
      const gamePlayer = getGamePlayer(selected);
      if (rosterPlayer) {
        Object.assign(gameState[key], toTaggedPlayerFields(rosterPlayer));
      } else if (gamePlayer) {
        Object.assign(gameState[key], toGamePlayerFields(gamePlayer));
      } else {
        // Not a picked option (the row still holds the stored name)
        gameState[key].playerName = stored.name || selected;
        if (stored.externalId) gameState[key].externalId = stored.externalId;
      }
    };

    applyPlayer("currentPitcher", row.pitcher, {
      name: storedNames.pitcherName as string | undefined,
      externalId: row.pitcherExternalId,
    });
    applyPlayer("currentHitter", row.hitter, {
      name: storedNames.hitterName as string | undefined,
      externalId: row.hitterExternalId,
    });

    if (row.pitchType && row.pitchType !== "Unknown") {
      payload.tag_data.metadata.pitchType = row.pitchType;
    } else if (row.pitchType === "Unknown") {
      // Clear pitchType if user selected "Unknown"
      payload.tag_data.metadata.pitchType = null;
    }

    applyRowResults(payload.tag_data.metadata, row.pitchResult, row.playResult);

    return {
      body: {
        payload,
      },
    };
  };

  const gameType = game?.type || "game";
  const isSoftball = game?.sport === "softball";

  const config = useMemo(() => {
    const gameTypeConfigs = buildGameTypeConfigs(isSoftball);
    return gameTypeConfigs[gameType] || gameTypeConfigs.game;
  }, [gameType, isSoftball]);

  // Normalize raw tags into table row data
  // Also build available innings for inning filter
  const { tagsData, availableInnings } = useTransformedTagsData({
    tags,
    game,
    config,
  });

  // Filter data based on available filters for this game type
  const filteredData = useMemo(() => {
    return tagsData.filter((row) => {
      // Inning filter (only for game type)
      const inningMatch =
        !config.filters.includes("inning") ||
        selectedInning === "all" ||
        row.inning === parseInt(selectedInning);

      // Half filter (only for game type)
      const halfMatch =
        !config.filters.includes("half") ||
        selectedHalf === "all" ||
        row.half === selectedHalf;

      // Pitch type filter
      const pitchTypeMatch =
        !config.filters.includes("pitchType") ||
        selectedPitchType === "all" ||
        row.pitchType === selectedPitchType;

      // Search filter (all types)
      const searchMatch =
        !config.filters.includes("search") ||
        searchQuery === "" ||
        (row.pitcher &&
          row.pitcher.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (row.hitter &&
          row.hitter.toLowerCase().includes(searchQuery.toLowerCase()));

      return inningMatch && halfMatch && pitchTypeMatch && searchMatch;
    });
  }, [
    tagsData,
    selectedInning,
    selectedHalf,
    selectedPitchType,
    searchQuery,
    config.filters,
  ]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * ITEMS_PER_PAGE;
    return filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredData, currentPage]);

  // Group data by inning and half for rendering (only for 'game' type)
  const groupedData = useMemo((): TableGroup<TagRowData>[] | TagRowData[] => {
    if (!config.showGrouping) {
      // No grouping - return flat data array
      return paginatedData;
    }

    // Group by inning and half - use a Map for more reliable grouping
    const groupMap = new Map<string, TagRowData[]>();

    paginatedData.forEach((row) => {
      // Create a consistent key for grouping
      const inning = row.inning ?? 0;
      const half = row.half ?? "top";
      const key = `${inning}-${half}`;

      const existing = groupMap.get(key);
      if (existing) {
        existing.push(row);
      } else {
        groupMap.set(key, [row]);
      }
    });

    // Convert Map to array of groups, sorted by inning and half
    const groups = Array.from(groupMap.entries())
      .map(([key, rows]) => {
        const [inningStr, half] = key.split("-");
        return {
          inning: parseInt(inningStr),
          half: half as "top" | "bottom",
          rows,
        };
      })
      .sort((a, b) => {
        // Sort by inning first
        if (a.inning !== b.inning) return a.inning - b.inning;
        // Then by half (top before bottom)
        return a.half === "top" ? -1 : 1;
      });

    return groups;
  }, [paginatedData, config.showGrouping]);

  // Populate columns with player options
  const columnsWithOptions = useMemo(() => {
    return config.columns.map((col) => {
      // Populate player select columns with available players
      if (col.editor === "searchable-select" && col.options?.length === 0) {
        // For pitcher/hitter, use getOptions to dynamically filter by team based on inning half
        if (col.key === "pitcher" || col.key === "hitter") {
          return {
            ...col,
            getOptions: (row: TagRowData) => {
              const field = col.key === "pitcher" ? "pitcher" : "hitter";
              return getPlayersForField(field, row.half);
            },
          };
        }
        // For other player selects, use all available players
        return { ...col, options: availablePlayers };
      }
      return col;
    });
  }, [config.columns, availablePlayers, getPlayersForField]);

  const pitchTypeOptions = useMemo(() => {
    const pitchTypeColumn = config.columns.find(
      (col) => col.key === "pitchType",
    );
    const options = pitchTypeColumn?.options || [];
    return [{ value: "all", label: "All", id: "all" }, ...options];
  }, [config.columns]);

  const handleSaveChanges = async (changedRows: TagRowData[]) => {
    if (changedRows.length === 0) return;

    setIsSaving(true);

    try {
      let hadError = false;

      for (let i = 0; i < changedRows.length; i += SAVE_BATCH_SIZE) {
        const batch = changedRows.slice(i, i + SAVE_BATCH_SIZE);

        const payloads: Array<NonNullable<ReturnType<typeof buildPayload>>> =
          [];
        for (const row of batch) {
          const req = buildPayload(row);
          if (!req) {
            hadError = true;
            break;
          }
          payloads.push(req);
        }

        if (payloads.length < batch.length) {
          break;
        }

        const results = await Promise.allSettled(
          payloads.map((req) => saveTag(req)),
        );

        results.forEach((result) => {
          if (result.status === "rejected") {
            hadError = true;
          }
        });
      }

      if (!hadError) {
        toast("Changes saved successfully", {
          variant: "success",
          duration: 6000,
        });

        // Delay invalidation to next tick to allow commitSave to run first
        setTimeout(() => {
          const queryKey = getGameByGameIdEventsOptions({
            path: { game_id: gameId ?? "" },
          }).queryKey;
          queryClient.invalidateQueries({ queryKey });
        }, 0);
      } else {
        toast("Some changes could not be saved. Please try again.", {
          variant: "error",
          duration: 6000,
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col gap-6 w-full py-6 px-16 font-manrope">
        <AlertMessage
          variant="destructive"
          title="Unable to load event data"
          description="There was a problem loading the event data. Please try refreshing the page or go back to select a different event."
        />
      </div>
    );
  }

  // Wait for rosters too, so player dropdowns never open without them
  if (isLoading || isLoadingRosters) {
    return (
      <div className="flex flex-col gap-6 w-full py-6 px-16 font-manrope">
        <div className="flex justify-center items-center py-12">
          <Spinner />
        </div>
      </div>
    );
  }

  // Render group headers for EditableTable
  const renderGroupHeader = (group: TableGroup<TagRowData>) => {
    if (!config.showGrouping) return null;

    const { inning, half } = group as unknown as {
      inning: number;
      half: "top" | "bottom";
    };
    const isTopHalf = half === "top";
    const halfLabel = isTopHalf ? "Top" : "Bottom";

    return [
      // Show "Inning X" header only for top half
      isTopHalf && (
        <TableRow
          key={`inning-${inning}`}
          clickable={false}
          className="border-gray-200"
        >
          <TableCell
            colSpan={columnsWithOptions.length}
            className="font-semibold"
          >
            Inning {inning}
          </TableCell>
        </TableRow>
      ),

      // Show "Top/Bottom X" header for each half
      <TableRow
        key={`${half}-${inning}`}
        clickable={false}
        className="border-gray-200"
      >
        <TableCell colSpan={columnsWithOptions.length} className="font-medium">
          {halfLabel} {inning}
        </TableCell>
      </TableRow>,
    ].filter(Boolean);
  };

  return (
    <div className="flex flex-col gap-6 w-full py-6 px-16 font-manrope">
      {game?.generated_display_name && (
        <NavbarDescriptionPortal>
          {game.generated_display_name}
        </NavbarDescriptionPortal>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {config.filters.includes("inning") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Filter by Inning</span>
            <SearchableSelect
              value={selectedInning}
              onValueChange={setSelectedInning}
              options={availableInnings}
              placeholder="All Innings"
            />
          </div>
        )}

        {config.filters.includes("half") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Filter by Half</span>
            <SearchableSelect
              value={selectedHalf}
              onValueChange={setSelectedHalf}
              options={HALF_OPTIONS}
              placeholder="All"
            />
          </div>
        )}

        {config.filters.includes("pitchType") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Filter by Pitch Type</span>
            <SearchableSelect
              value={selectedPitchType}
              onValueChange={setSelectedPitchType}
              options={pitchTypeOptions}
              placeholder="All"
            />
          </div>
        )}

        {config.filters.includes("search") && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Filter by Player</span>
            <Input
              type="text"
              placeholder="Search pitcher or hitter name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        )}
      </div>

      <EditableTable
        data={groupedData}
        columns={columnsWithOptions}
        keyExtractor={(row) => row.eventUuid}
        onSave={handleSaveChanges}
        renderGroupHeader={renderGroupHeader}
        isGrouped={config.showGrouping}
        currentPage={currentPage}
        totalPages={Math.ceil(filteredData.length / ITEMS_PER_PAGE)}
        totalItems={filteredData.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setCurrentPage}
        itemLabel="pitches"
        isSaving={isSaving}
      />
    </div>
  );
};

export default EditEventLog;
